// src/components/EmployeeList.js - Combined table and mini-card design
import React, { useState, useEffect, useRef } from "react";
import Icon from "./icons/Icon";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useEmployees } from "../hooks/useEmployees";
import { toYMDString } from "../utils/periodDate";
import { isEmployeeActiveToday } from "../utils/employeeFilters";
import { formatFilteredCount } from "../utils/footerStats";
import { useToast } from "../contexts/ToastContext";

export const EmployeeList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company, hasPermission } = useAuth();
  const canAddEmployee = hasPermission('emp:edit');
  const toast = useToast();
  const {
    employees,
    loading,
    error,
    searchEmployees,
    stats,
    clearError,
  } = useEmployees(company?.id);

  // Local state for UI
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle responsive view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Check initial size
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle success message from navigation state
  const handledMessageRef = useRef(null);
  useEffect(() => {
    const message = location.state?.message;
    if (message && message !== handledMessageRef.current) {
      handledMessageRef.current = message;
      const type = location.state.type || "success";
      if (type === "success") {
        toast.success(message);
      } else {
        toast.error(message);
      }

      // Clear the navigation state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname, toast]);

  // Clear error when component mounts
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [error, clearError]);

  // Filter employees based on search and filters
  const filteredEmployees = searchEmployees(searchTerm, {
    department: filterDepartment,
    status: filterStatus,
  });

  // Filter-aware footer stats (PRD_FilterAwareFooterStats C1/C3). The predicate
  // mirrors searchEmployees' own trimmed search condition (employeeSchema.js).
  // Page-wide `stats` stays untouched — it is the "of N" base and the source of
  // the department dropdown options.
  const filterActive =
    Boolean(searchTerm.trim()) || Boolean(filterDepartment) || Boolean(filterStatus);
  const displayedActive = filteredEmployees.filter((e) =>
    isEmployeeActiveToday(e)
  ).length;
  const displayedStats = {
    active: displayedActive,
    inactive: filteredEmployees.length - displayedActive,
    departmentsCount: new Set(
      filteredEmployees.map((e) => e.employment?.department).filter(Boolean)
    ).size,
  };
  const displayedTotal = formatFilteredCount(
    filteredEmployees.length,
    stats.total,
    filterActive
  );

  // Sort employees
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    if (!sortField) return 0;

    let aValue, bValue;
    switch (sortField) {
      case "name":
        // Sort by lastName first, then firstName as secondary sort
        aValue = (a.personalInfo?.lastName || '').toLowerCase();
        bValue = (b.personalInfo?.lastName || '').toLowerCase();
        if (aValue === bValue) {
          aValue = (a.personalInfo?.firstName || '').toLowerCase();
          bValue = (b.personalInfo?.firstName || '').toLowerCase();
        }
        break;
      case "id":
        aValue = a.employeeId;
        bValue = b.employeeId;
        break;
      case "department":
        aValue = a.employment?.department || "";
        bValue = b.employment?.department || "";
        break;
      case "startDate":
        aValue = a.employment?.startDate?.seconds || a.employment?.startDate || 0;
        bValue = b.employment?.startDate?.seconds || b.employment?.startDate || 0;
        break;
      case "status":
        // Derived from the employment dates, not status.current (BL-075).
        aValue = isEmployeeActiveToday(a) ? "active" : "inactive";
        bValue = isEmployeeActiveToday(b) ? "active" : "inactive";
        break;
      default:
        return 0;
    }

    // localeCompare for strings: a raw < / > compares character codes, so Ñ ranks
    // above every plain letter and Ñuñez sorts last. Numbers keep the raw compare.
    // Matches PayrollSummary / DeductionsStep / GrossIncomeBreakdown, which already do this.
    const c = typeof aValue === "string" && typeof bValue === "string"
      ? aValue.localeCompare(bValue)
      : aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return sortDirection === "asc" ? c : -c;
  });

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle employee row/card click — ALWAYS the detail page, for everyone.
  //
  // It used to branch: emp:edit holders went straight to the Edit wizard and
  // only non-editors reached the detail page. Since that click is the app's
  // ONLY navigation to the detail page, an admin could never open it at all —
  // its tabs and its Reset Kiosk PIN button were unreachable for the people who
  // own those jobs, and a Loans tab added there shipped invisible
  // (PRD_LoansManagement C12, /test-impl 2026-09-26; the tab was withdrawn and
  // this routing fixed in the same cycle rather than deferred).
  //
  // The detail page is built to be the landing screen: it already carries an
  // `emp:edit`-gated "Edit Employee" button to the wizard, so editors lose
  // nothing but a click. Both routes keep their own permission guards.
  const handleEmployeeClick = (employeeId) => {
    navigate(`/company/${company.slug}/employees/${employeeId}/view`);
  };

  if (!user || !company) {
    return (
      <div className="page">
        <div className="loading">
          <div className="loading__spinner--ph"></div>
          <div className="loading__text">Loading employees...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "var(--font-family-primary)" }}>
      <div style={{ width: "100%" }}>
        {/* Slim Page Title */}
        <h1 style={{
          fontSize: isMobile ? "1.125rem" : "1.25rem",
          fontWeight: "700",
          color: "var(--ui-primary)",
          margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0",
        }}>
          Employee Management
        </h1>

        {/* Stats Cards - mobile only (desktop stats moved to table footer) */}
        {isMobile && <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "1.25rem 1.25rem 1.25rem 1.5rem",
              border: "1px solid var(--ui-border)",
              borderLeft: "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{
              position: "absolute",
              left: 0,
              top: "15%",
              bottom: "15%",
              width: "4px",
              background: "var(--ui-primary)",
              borderRadius: "0 4px 4px 0",
            }} />
            <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>
              Total Employees
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>
              {displayedTotal}
            </div>
          </div>
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "1.25rem 1.25rem 1.25rem 1.5rem",
              border: "1px solid var(--ui-border)",
              borderLeft: "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{
              position: "absolute",
              left: 0,
              top: "15%",
              bottom: "15%",
              width: "4px",
              background: "var(--ui-success)",
              borderRadius: "0 4px 4px 0",
            }} />
            <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>
              Active
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>
              {displayedStats.active}
            </div>
          </div>
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "1.25rem 1.25rem 1.25rem 1.5rem",
              border: "1px solid var(--ui-border)",
              borderLeft: "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{
              position: "absolute",
              left: 0,
              top: "15%",
              bottom: "15%",
              width: "4px",
              background: "var(--ui-danger)",
              borderRadius: "0 4px 4px 0",
            }} />
            <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>
              Inactive
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>
              {displayedStats.inactive}
            </div>
          </div>
          <div
            style={{
              background: "var(--ui-primary)",
              borderRadius: "8px",
              padding: "1.25rem",
            }}
          >
            <div style={{ fontSize: "0.6875rem", color: "rgba(255,255,255,0.8)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>
              Departments
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "white" }}>
              {displayedStats.departmentsCount}
            </div>
          </div>
        </div>}

        {/* Controls - Compact bar (desktop) / Stacked (mobile) */}
        {isMobile ? (
          <div style={{
            background: "white",
            padding: "1rem",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            marginBottom: "0.75rem",
          }}>
            <input
              type="text"
              placeholder="Search employees (name, ID, email)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                marginBottom: "0.5rem",
              }}
            />
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                marginBottom: "0.5rem",
                background: "white",
              }}
            >
              <option value="">All Departments</option>
              {stats.departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                marginBottom: "0.5rem",
                background: "white",
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {canAddEmployee && (
              <button
                onClick={() => navigate(`/company/${company.slug}/employees/add`)}
                style={{
                  width: "100%",
                  backgroundColor: "var(--ui-primary)",
                  color: "white",
                  padding: "0.5rem 1rem",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontSize: "0.8125rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.375rem",
                }}
              >
                + Add Employee
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: "0.625rem 1rem",
            background: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            marginBottom: "0.75rem",
          }}>
            <input
              type="text"
              placeholder="Search employees (name, ID, email)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: "160px",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
              }}
            />
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={{
                minWidth: "140px",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                background: "white",
                cursor: "pointer",
              }}
            >
              <option value="">All Departments</option>
              {stats.departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                minWidth: "120px",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                background: "white",
                cursor: "pointer",
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {canAddEmployee && (
              <>
                {/* Divider */}
                <div style={{ width: "1px", height: "24px", background: "var(--ui-border)", flexShrink: 0 }} />
                <button
                  onClick={() => navigate(`/company/${company.slug}/employees/add`)}
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "var(--ui-primary)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontSize: "0.8125rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  + Add Employee
                </button>
              </>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "3px solid var(--ui-border)",
                borderTop: "3px solid var(--ui-primary)",
                borderRadius: "50%",
                margin: "0 auto 1rem",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <p style={{ color: "var(--ui-text-muted)" }}>Loading employees...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && employees.length === 0 && (
          <EmptyState
            onAddEmployee={() =>
              navigate(`/company/${company.slug}/employees/add`)
            }
          />
        )}

        {/* No Results State */}
        {!loading && employees.length > 0 && sortedEmployees.length === 0 && (
          <NoResultsState
            onClearFilters={() => {
              setSearchTerm("");
              setFilterDepartment("");
              setFilterStatus("");
            }}
          />
        )}

        {/* Employee Cards - Mobile only */}
        {!loading && isMobile && sortedEmployees.length > 0 && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
                gap: isMobile ? "1rem" : "1rem",
              }}
            >
              {sortedEmployees.map((employee) => (
                <MiniEmployeeCard
                  key={employee.id}
                  employee={employee}
                  onClick={handleEmployeeClick}
                />
              ))}
            </div>

            {/* Card View Footer */}
            <div
              style={{
                marginTop: "16px",
                padding: "16px 20px",
                background: "linear-gradient(135deg, var(--ui-bg), var(--ui-surface-recessed))",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid var(--ui-border)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div style={{ display: "flex", gap: "28px", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
                    Total Employees
                  </div>
                  <div style={{ fontSize: "20px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-primary)" }}>
                    {displayedTotal}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
                    Active
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>
                    {displayedStats.active}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
                    Inactive
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>
                    {displayedStats.inactive}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
                    Departments
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>
                    {displayedStats.departmentsCount}
                  </div>
                </div>
              </div>
              <span style={{ fontSize: "13px", fontStyle: "italic", color: "var(--ui-text-muted)" }}>
                Click on a row to view details
              </span>
            </div>
          </>
        )}

        {/* Employee Table - Desktop only */}
        {!loading && !isMobile && sortedEmployees.length > 0 && (
          <EmployeeTable
            employees={sortedEmployees}
            displayedStats={displayedStats}
            displayedTotal={displayedTotal}
            onClick={handleEmployeeClick}
            onSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
          />
        )}

        {/* Inline Styles for Animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

// Mini Employee Card Component
const MiniEmployeeCard = ({ employee, onClick }) => {
  // Employment dates are the source of truth — status.current is not read (BL-075).
  const isActive = isEmployeeActiveToday(employee);

  return (
    <div
      onClick={() => onClick(employee.employeeId)}
      style={{
        background: "white",
        borderRadius: "10px",
        padding: "1rem",
        border: "2px solid var(--ui-border)",
        cursor: "pointer",
        transition: "all 0.2s",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--ui-primary)";
        e.currentTarget.style.boxShadow = "var(--ui-shadow-primary)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--ui-border)";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Status dot */}
      <span
        style={{
          position: "absolute",
          top: "0.5rem",
          right: "0.5rem",
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: isActive ? "var(--ui-success)" : "var(--ui-danger)",
        }}
      />

      {/* Card header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "0.75rem",
        }}
      >
        <div
          style={{
            width: "36px",
            height: "36px",
            background: "var(--ui-primary-tint)",
            color: "var(--ui-primary)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "600",
            fontSize: "13px",
            flexShrink: 0,
          }}
        >
          {employee.personalInfo?.firstName?.charAt(0)}
          {employee.personalInfo?.lastName?.charAt(0)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "0.9375rem",
              fontWeight: "600",
              color: "var(--ui-text)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {employee.personalInfo?.lastName}, {employee.personalInfo?.firstName}
            </span>
            {employee.hasPii === false && (
              <span
                title="Payroll info incomplete — won't be included in payroll runs until salary, banking, and gov IDs are added"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "var(--ui-warning-tint)",
                  color: "var(--ui-warning)",
                  fontSize: "11px",
                  fontWeight: "700",
                  flexShrink: 0,
                  cursor: "help",
                }}
              >
                !
              </span>
            )}
          </div>
          <div
            style={{
              fontVariantNumeric: "tabular-nums",
              fontSize: "0.75rem",
              color: "var(--ui-text-muted)",
            }}
          >
            ID: {employee.employeeId}
          </div>
        </div>
      </div>

      {/* Card details */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--ui-text-muted)" }}>
          <span style={{ fontWeight: "500" }}>Department:</span>
          <span
            style={{
              color: employee.employment?.department ? "var(--ui-text-muted)" : "var(--ui-text-faint)",
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "60%",
              textAlign: "right",
            }}
          >
            {employee.employment?.department || "Not set"}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--ui-text-muted)" }}>
          <span style={{ fontWeight: "500" }}>Start Date:</span>
          {/* One format regardless of stored type. This branched on
              startDate.seconds and rendered "Aug 19, 2026" for a Timestamp but
              the raw string for a 'YYYY-MM-DD' — so the same column showed two
              formats depending on how the employee happened to be created, and
              this column is the one the rest of the app was told to match. */}
          <span style={{ color: "var(--ui-text-secondary)", textAlign: "right" }}>
            {toYMDString(employee.employment?.startDate) || "Not set"}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--ui-text-muted)" }}>
          <span style={{ fontWeight: "500" }}>Status:</span>
          <span
            style={{
              background: isActive ? "var(--ui-success-tint)" : "var(--ui-danger-tint)",
              color: isActive ? "var(--ui-success-text)" : "var(--ui-danger-text)",
              padding: "0.125rem 0.5rem",
              borderRadius: "12px",
              fontSize: "0.75rem",
              fontWeight: "600",
              textTransform: "capitalize",
              textAlign: "right",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>
    </div>
  );
};

// Sort Icon Component
const SortIcon = ({ field, sortField, sortDirection }) => {
  const isActive = sortField === field;
  const color = "white";

  if (isActive && sortDirection === "asc") {
    return (
      <Icon name="arrow-up" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
    );
  }

  if (isActive && sortDirection === "desc") {
    return (
      <Icon name="arrow-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
    );
  }

  // Default: show both arrows (unsorted)
  return (
    <Icon name="chevrons-up-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.5 }} />
  );
};

// Employee Table Component
const EmployeeTable = ({ employees, displayedStats, displayedTotal, onClick, onSort, sortField, sortDirection }) => {

  const thStyle = {
    padding: "14px 16px",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "white",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "var(--ui-gradient-primary)" }}>
            <th onClick={() => onSort("name")} style={thStyle}>
              Employee<SortIcon field="name" sortField={sortField} sortDirection={sortDirection} />
            </th>
            <th onClick={() => onSort("id")} style={thStyle}>
              ID<SortIcon field="id" sortField={sortField} sortDirection={sortDirection} />
            </th>
            <th onClick={() => onSort("department")} style={thStyle}>
              Department<SortIcon field="department" sortField={sortField} sortDirection={sortDirection} />
            </th>
            <th onClick={() => onSort("startDate")} style={thStyle}>
              Start Date<SortIcon field="startDate" sortField={sortField} sortDirection={sortDirection} />
            </th>
            <th onClick={() => onSort("status")} style={thStyle}>
              Status<SortIcon field="status" sortField={sortField} sortDirection={sortDirection} />
            </th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => {
            // Employment dates are the source of truth — status.current is not read (BL-075).
            const isActive = isEmployeeActiveToday(employee);

            return (
              <tr
                key={employee.id}
                onClick={() => onClick(employee.employeeId)}
                style={{
                  borderBottom: "1px solid var(--ui-surface-recessed)",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--ui-primary-tint)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        background: "var(--ui-primary-tint)",
                        color: "var(--ui-primary)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "600",
                        fontSize: "13px",
                        flexShrink: 0,
                      }}
                    >
                      {employee.personalInfo?.firstName?.charAt(0)}
                      {employee.personalInfo?.lastName?.charAt(0)}
                    </div>
                    <span style={{ fontWeight: "500", color: "var(--ui-text)", fontSize: "0.875rem" }}>
                      {employee.personalInfo?.lastName}, {employee.personalInfo?.firstName}
                    </span>
                    {employee.hasPii === false && (
                      <span
                        title="Payroll info incomplete — won't be included in payroll runs until salary, banking, and gov IDs are added"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          background: "var(--ui-warning-tint)",
                          color: "var(--ui-warning)",
                          fontSize: "11px",
                          fontWeight: "700",
                          flexShrink: 0,
                          cursor: "help",
                          marginLeft: "4px",
                        }}
                      >
                        !
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ padding: "12px 16px", fontVariantNumeric: "tabular-nums", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                  {employee.employeeId}
                </td>
                <td style={{ padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: employee.employment?.department ? "var(--ui-text)" : "var(--ui-text-faint)" }}>
                  {employee.employment?.department || "Not set"}
                </td>
                <td style={{ padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                  {toYMDString(employee.employment?.startDate)
                    || <span style={{ color: "var(--ui-text-faint)", fontWeight: "400" }}>Not set</span>}
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <span
                    style={{
                      background: isActive ? "var(--ui-success-tint)" : "var(--ui-danger-tint)",
                      color: isActive ? "var(--ui-success-text)" : "var(--ui-danger-text)",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "20px",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      textTransform: "capitalize",
                    }}
                  >
                    {isActive ? "Active" : "Inactive"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Table Footer */}
      <div
        style={{
          padding: "16px 20px",
          background: "linear-gradient(135deg, var(--ui-bg), var(--ui-surface-recessed))",
          borderTop: "1px solid var(--ui-border)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", gap: "28px" }}>
          <div>
            <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
              Total Employees
            </div>
            <div style={{ fontSize: "20px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-primary)" }}>
              {displayedTotal}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
              Active
            </div>
            <div style={{ fontSize: "16px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>
              {displayedStats.active}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
              Inactive
            </div>
            <div style={{ fontSize: "16px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>
              {displayedStats.inactive}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>
              Departments
            </div>
            <div style={{ fontSize: "16px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>
              {displayedStats.departmentsCount}
            </div>
          </div>
        </div>
        <span style={{ fontSize: "13px", fontStyle: "italic", color: "var(--ui-text-muted)" }}>
          Click on a row to view details
        </span>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ onAddEmployee }) => {
  return (
    <div
      style={{
        background: "white",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: "1px solid var(--ui-border)",
        overflow: "hidden",
      }}
    >
      {/* Blue header bar to match table style */}
      <div
        style={{
          backgroundColor: "var(--ui-primary)",
          padding: "0.875rem 1.5rem",
        }}
      >
        <span style={{ color: "white", fontWeight: "600", fontSize: "0.875rem" }}>
          Employee Directory
        </span>
      </div>

      {/* Empty state content */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "4rem 2rem",
          textAlign: "center",
        }}
      >
        {/* Illustration */}
        <div
          style={{
            width: "120px",
            height: "120px",
            background: "var(--ui-primary-tint)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.5rem",
            border: "3px solid var(--ui-primary)",
          }}
        >
          <Icon name="users" size={56} />
        </div>

        <h3
          style={{
            fontSize: "1.5rem",
            fontWeight: "700",
            color: "var(--ui-primary)",
            marginBottom: "0.5rem",
            margin: "0 0 0.5rem 0",
          }}
        >
          No Employees Yet
        </h3>

        <p
          style={{
            color: "var(--ui-text-muted)",
            marginBottom: "2rem",
            lineHeight: 1.6,
            maxWidth: "400px",
            fontSize: "0.9375rem",
          }}
        >
          Get started by adding your first employee. Track their information,
          manage schedules, and process payroll all in one place.
        </p>

        {/* Feature highlights */}
        <div
          style={{
            display: "flex",
            gap: "2rem",
            marginBottom: "2rem",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "var(--ui-success-tint)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 0.5rem",
                border: "1px solid var(--ui-border)",
              }}
            >
              <Icon name="file-text" size={20} />
            </div>
            <span style={{ fontSize: "0.75rem", color: "var(--ui-text-secondary)", fontWeight: "500" }}>
              Personal Info
            </span>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "var(--ui-primary-tint)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 0.5rem",
                border: "1px solid var(--ui-border)",
              }}
            >
              <Icon name="briefcase" size={20} />
            </div>
            <span style={{ fontSize: "0.75rem", color: "var(--ui-text-secondary)", fontWeight: "500" }}>
              Employment
            </span>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "var(--ui-warning-tint)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 0.5rem",
                border: "1px solid var(--ui-border)",
              }}
            >
              <Icon name="dollar-sign" size={20} />
            </div>
            <span style={{ fontSize: "0.75rem", color: "var(--ui-text-secondary)", fontWeight: "500" }}>
              Payroll Setup
            </span>
          </div>
        </div>

        <button
          onClick={onAddEmployee}
          style={{
            backgroundColor: "var(--ui-primary)",
            color: "white",
            padding: "0.875rem 2rem",
            border: "none",
            borderRadius: "8px",
            fontWeight: "600",
            cursor: "pointer",
            fontSize: "0.9375rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--ui-primary-hover)"}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "var(--ui-primary)"}
        >
          + Add Your First Employee
        </button>
      </div>
    </div>
  );
};

// No Results State Component
const NoResultsState = ({ onClearFilters }) => {
  return (
    <div
      style={{
        background: "white",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: "1px solid var(--ui-border)",
        overflow: "hidden",
      }}
    >
      {/* Blue header bar to match table style */}
      <div
        style={{
          backgroundColor: "var(--ui-primary)",
          padding: "0.875rem 1.5rem",
        }}
      >
        <span style={{ color: "white", fontWeight: "600", fontSize: "0.875rem" }}>
          Search Results
        </span>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "3rem 2rem",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "80px",
            height: "80px",
            background: "var(--ui-surface-recessed)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1rem",
          }}
        >
          <Icon name="search" size={36} />
        </div>
        <h3
          style={{
            fontSize: "1.125rem",
            fontWeight: "600",
            color: "var(--ui-text-secondary)",
            marginBottom: "0.5rem",
            margin: "0 0 0.5rem 0",
          }}
        >
          No Employees Found
        </h3>
        <p
          style={{
            color: "var(--ui-text-muted)",
            marginBottom: "1.5rem",
            fontSize: "0.875rem",
            maxWidth: "300px",
          }}
        >
          No employees match your search criteria. Try adjusting your filters.
        </p>
        <button
          onClick={onClearFilters}
          style={{
            backgroundColor: "var(--ui-primary)",
            color: "white",
            padding: "0.625rem 1.25rem",
            border: "none",
            borderRadius: "6px",
            fontWeight: "600",
            cursor: "pointer",
            fontSize: "0.875rem",
          }}
        >
          Clear All Filters
        </button>
      </div>
    </div>
  );
};